<template>
  <div class="documents-summary">
    <div v-if="isLoading" class="documents-summary__empty">
      {{ $t('documents-summary.evaluation-grid-template-loading') }}
    </div>
    <div v-else-if="!hasEvaluationFields && !hasEvaluationGridTemplate" class="documents-summary__empty">
      {{ $t('documents-summary.no-evaluation-grid-template') }}
    </div>
    <template v-else>
      <BaseInfoBanner
        v-if="hasEvaluationFields && aiSelectedDocumentCount > 0"
        tone="ai"
        preset="soft"
        :title="$tc('documents-summary.ai-banner-title', aiSelectedDocumentCount, { count: aiSelectedDocumentCount })"
        :description="$t('documents-summary.ai-banner-description')"
      >
        <template #icon>
          <BaseIcon name="ai" />
        </template>
        <template #action>
          <button @click="openExtractionDocumentsDialog">
            {{ $t('documents-summary.ai-banner-action') }}
          </button>
        </template>
      </BaseInfoBanner>
      <ExtractionDocumentsDialog ref="extractionDocumentsDialog" @submit="onExtractionDocumentsSubmit" />
      <div v-if="!hasEvaluationFields" class="documents-summary__empty">
        {{ $t('documents-summary.no-results') }}
      </div>
      <div style="padding-top: 12px">
        <DocumentsSummaryItem
          v-for="(field, index) in evaluationFields"
          :key="field.id || index"
          :item="field"
          @on-add="addValue"
          @on-delete="deleteValue"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isXlsFile } from '@/util/document-file-util';
import DocumentsSummaryItem from '@/components/page-opportunity-details/DocumentsSummaryItem.vue';
import BaseInfoBanner from '@/components/base/BaseInfoBanner.vue';
import ExtractionDocumentsDialog from '@/components/page-opportunity-details/ExtractionDocumentsDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';

export default {
  name: 'DocumentsSummary',
  components: {
    BaseIcon,
    DocumentsSummaryItem,
    BaseInfoBanner,
    ExtractionDocumentsDialog,
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters('evaluationGrid', { evaluationGridLoading: 'pending', evaluationFields: 'evaluationFields' }),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('evaluationGridTemplates', ['evaluationGridTemplates']),
    ...mapGetters('evaluationGridTemplates', { evaluationGridTemplatesLoading: 'pending' }),
    ...mapGetters('fieldsExtraction', ['documentsUsed']),
    hasDocuments() {
      return this.opportunity && this.opportunity.documents && this.opportunity.documents.length > 0;
    },
    hasPDFDocument() {
      return this.hasDocuments && this.opportunity.documents.find((document) => !isXlsFile(document));
    },
    hasEvaluationFields() {
      return Array.isArray(this.evaluationFields) && this.evaluationFields.length > 0;
    },
    hasEvaluationGridTemplate() {
      return (
        this.evaluationGridTemplates &&
        this.evaluationGridTemplates.length > 0 &&
        this.evaluationGridTemplates[0].fields &&
        this.evaluationGridTemplates[0].fields.length > 0
      );
    },
    // Avoid blanking an already-visible grid when templates are merely refreshing,
    // or when the grid is re-fetched but previous fields are still available.
    isLoading() {
      if (this.hasEvaluationFields) {
        return false;
      }
      return this.evaluationGridLoading || this.evaluationGridTemplatesLoading;
    },
    // get the documents that were used in the last extraction and that still exist in the opportunity (ie didn't get deleted)
    aiSelectedDocumentCount() {
      if (!this.opportunity || !this.opportunity.documents || !this.documentsUsed) {
        return 0;
      }
      return this.opportunity.documents.filter((doc) => this.documentsUsed.some((used) => used.id === doc.id)).length;
    },
  },
  methods: {
    ...mapActions('evaluationGrid', ['deleteEvalGridValue', 'addEvalGridValue']),
    ...mapActions('fieldsExtraction', ['startExtraction']),
    openExtractionDocumentsDialog() {
      this.$refs.extractionDocumentsDialog.openDialog();
    },
    onExtractionDocumentsSubmit(selectedDocuments) {
      if (!this.opportunity || selectedDocuments.length === 0) {
        return;
      }
      this.startExtraction({
        opportunityId: this.opportunity.id,
        evaluationGridTemplateId: null,
        documentIds: selectedDocuments.map((doc) => doc.id),
        enableTriage: false,
      });
    },
    addValue({ fieldId, value }) {
      this.addEvalGridValue({
        opportunityId: this.opportunity.id,
        fieldId,
        source: 'NO_SOURCE',
        textAnnotationId: null,
        opportunityDocumentId: null,
        value,
        valueType: 'VALUE',
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => this.$toast.error(error));
    },
    deleteValue({ fieldId, value }) {
      this.deleteEvalGridValue({
        opportunityId: this.opportunity.id,
        fieldId,
        value,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => this.$toast.error(error));
    },
  },
};
</script>
